import { useState } from 'react';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsCard,
  PortalDsSectionHeader,
  portalDs,
} from 'src/components/portal-design-system';
import {
  useClearPerformanceCacheMutation,
  useClearReportingApiCacheMutation,
} from 'src/redux/operations/operations.endpoints';

type CacheKind = 'performance' | 'reporting-api' | 'surveys-api';

function getErrorMessage(error: unknown, fallback: string): string {
  return (error as { message?: string; data?: { status?: { status_message?: string } } })?.data
    ?.status?.status_message
    ?? (error as { message?: string })?.message
    ?? fallback;
}

function formatDeletedMessage(deletedTotal: number, noun: string, nextStep: string): string {
  if (deletedTotal === 1) {
    return `Cleared 1 cached ${noun}. ${nextStep}`;
  }
  return `Cleared ${deletedTotal} cached ${noun}. ${nextStep}`;
}

export function SettingsCachesSection() {
  const [clearPerformanceCache, performanceState] = useClearPerformanceCacheMutation();
  const [clearReportingApiCache, reportingState] = useClearReportingApiCacheMutation();
  const [activeKind, setActiveKind] = useState<CacheKind | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleClearPerformance = async() => {
    setActiveKind('performance');
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const result = await clearPerformanceCache().unwrap();
      setSuccessMessage(formatDeletedMessage(
        result.deleted_total ?? 0,
        'performance snapshot',
        'The next Performance page load will fetch fresh data.'
      ));
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Could not clear the performance cache.'));
    } finally {
      setActiveKind(null);
    }
  };

  const handleClearReportingApi = async() => {
    setActiveKind('reporting-api');
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const result = await clearReportingApiCache().unwrap();
      setSuccessMessage(formatDeletedMessage(
        result.deleted_total ?? 0,
        'Reporting API entry',
        'The next Reporting API request will recompute from the warehouse.'
      ));
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Could not clear the Reporting API cache.'));
    } finally {
      setActiveKind(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <PortalDsCard aria-labelledby="settings-caches-performance-heading">
        <PortalDsSectionHeader
          overline="Caches"
          title="Performance"
          titleId="settings-caches-performance-heading"
          description="Clears Redis snapshots used by the Performance overview so the next visit recomputes from the warehouse."
          actions={(
            <PortalDsButton
              type="button"
              variant="accent"
              size="sm"
              onClick={() => void handleClearPerformance()}
              disabled={performanceState.isLoading || reportingState.isLoading}
            >
              {activeKind === 'performance' ? 'Clearing…' : 'Clear cache'}
            </PortalDsButton>
          )}
        />

        <p className={portalDs.bodySm}>
          Use this after ETL or data fixes when the overview still shows stale numbers.
          Aggregate, time-series, and account-filter caches are all removed.
        </p>
      </PortalDsCard>

      <PortalDsCard aria-labelledby="settings-caches-reporting-api-heading">
        <PortalDsSectionHeader
          overline="Caches"
          title="Reporting API"
          titleId="settings-caches-reporting-api-heading"
          description="Clears Redis entries for the external Reporting API (query payloads and dataset data versions)."
          actions={(
            <PortalDsButton
              type="button"
              variant="accent"
              size="sm"
              onClick={() => void handleClearReportingApi()}
              disabled={performanceState.isLoading || reportingState.isLoading}
            >
              {activeKind === 'reporting-api' ? 'Clearing…' : 'Clear cache'}
            </PortalDsButton>
          )}
        />

        <p className={portalDs.bodySm}>
          Use this when API clients still receive stale report results after ETL or entitlement
          changes. If the portal Performance surface is running in common API mode, clear this
          cache as well.
        </p>
      </PortalDsCard>

      <PortalDsCard
        aria-labelledby="settings-caches-surveys-api-heading"
        className="opacity-50"
      >
        <PortalDsSectionHeader
          overline="Caches"
          title="Surveys API"
          titleId="settings-caches-surveys-api-heading"
          description="Clears Redis entries for the external Surveys API. Not available yet."
          actions={(
            <PortalDsButton
              type="button"
              variant="accent"
              size="sm"
              disabled
            >
              Clear cache
            </PortalDsButton>
          )}
        />

        <p className={portalDs.bodySm}>
          Survey API cache clearing will be enabled when that API ships.
        </p>
      </PortalDsCard>

      {errorMessage ? (
        <PortalDsAlert
          variant="danger"
          title="Could not clear cache"
        >
          {errorMessage}
        </PortalDsAlert>
      ) : null}

      {successMessage ? (
        <PortalDsAlert
          variant="success"
          title="Cache cleared"
        >
          {successMessage}
        </PortalDsAlert>
      ) : null}
    </div>
  );
}
